// src/pages/clientes/components/ClienteInfo.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Button, Badge, Avatar } from '@/components/ui';
import { ArrowLeft, Pencil, Trash2, Building2, Hash, Settings } from 'lucide-react';
import { ClienteFormModal } from './ClienteFormModal';
import { DeleteClienteModal } from './DeleteClienteModal';
import type { Cliente } from '@/types';

const modoLabels: Record<string, string> = {
  SIMPLE: 'Simple',
  ESTANDAR: 'Estándar',
  CORPORATIVO: 'Corporativo',
};

interface Props {
  cliente: Cliente;
}

export function ClienteInfo({ cliente }: Props) {
  const navigate = useNavigate();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/clientes')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-4">
            <Avatar fallback={cliente.nombre} size="lg" />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {cliente.nombre}
                </h1>
                <Badge variant={cliente.activo ? 'success' : 'default'}>
                  {cliente.activo ? 'Activo' : 'Inactivo'}
                </Badge>
              </div>
              {cliente.nombre_corto && (
                <p className="text-gray-500">{cliente.nombre_corto}</p>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsEditOpen(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            Editar
          </Button>
          <Button variant="outline" onClick={() => setIsDeleteOpen(true)}>
            <Trash2 className="h-4 w-4 mr-2 text-red-500" />
            Eliminar
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                <Hash className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Código</p>
                <p className="text-sm font-medium font-mono">{cliente.codigo}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                <Settings className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Modo Operación</p>
                <p className="text-sm font-medium">
                  {modoLabels[cliente.modo_operacion] || cliente.modo_operacion}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                <Building2 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Empresas</p>
                <p className="text-sm font-medium">
                  {cliente.empresas?.length ?? 0} empresa(s)
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <ClienteFormModal
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        cliente={cliente}
      />

      <DeleteClienteModal
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        cliente={cliente}
      />
    </>
  );
}
